import { Cart } from "@commercetools/platform-sdk"
import { Language } from "@sixty-six-north/i18n"
import { TFunction } from "i18next"
import {
  UnfulfillableLineItem,
  CartValidationResponse
} from "../../../cart/CartValidation"
import { ErrorType } from "../../../error/ErrorType"
import { variantData } from "../../../product/VariantProxy"
import { CartValidationState } from "../../state/CartValidationState"
import { CartValidationBase, cartSuccess } from "../../validation/FormValidator"
import {
  CartValidationResult,
  cartError
} from "../../validation/ValidationError"

const getSkuFromCart = (sku: string, cart: Cart) => {
  return cart.lineItems.find(it => it.variant.sku === sku)
}

export class LineItemValidation extends CartValidationBase {
  private readonly t: TFunction
  private readonly validateCart: (
    cartId: string
  ) => Promise<CartValidationResponse>
  private readonly language: Language
  constructor(
    t: TFunction,
    validateCart: (cartId: string) => Promise<CartValidationResponse>,
    language: Language
  ) {
    super("LineItemValidation")
    this.t = t
    this.validateCart = validateCart
    this.language = language
  }

  constructUnfulfillableItemsMessage = (
    unfulfillableItems: UnfulfillableLineItem[],
    cart: Cart
  ) => {
    const skuMessages = unfulfillableItems.map(it => {
      const product = getSkuFromCart(it.sku, cart)
      if (product !== undefined) {
        const { size, color } = variantData(product.variant, this.language)
        const quantityToRemove = product.quantity - it.availableQuantity
        return `${quantityToRemove} ${product?.name[this.language]} - ${color} ${this.t("in")} ${size}`
      }
      return ``
    })

    const lastSku = skuMessages.pop()
    return skuMessages.length >= 1
      ? skuMessages.join(`, `).concat(` ${this.t("and")} ${lastSku}`)
      : lastSku
  }

  validate(cart: Cart): CartValidationResult {
    if (cart) {
      return this.validateCart(cart.id)
        .then(cartValidationResult => {
          switch (cartValidationResult.status) {
            case CartValidationState.VALID:
              return cartSuccess(cart)

            case CartValidationState.UNFULFILLABLE_CART:
              return cartError(ErrorType.CartValidationError, {
                message: this.t(
                  "checkout:yourBagHasUnfulfillableItemsErrorMessage",
                  {
                    listOfUnavailableItems:
                      this.constructUnfulfillableItemsMessage(
                        cartValidationResult.unfulfillableLineItems,
                        cart
                      )
                  }
                )
              })

            case CartValidationState.CART_NOT_FOUND:
              return cartError(ErrorType.CartNotFoundError, {
                message: this.t("checkout:yourBagIsEmpty")
              })

            case CartValidationState.ERROR:
              return cartError(ErrorType.CartValidationError, {
                message: this.t("checkout:yourCartIsInvalid")
              })

            case CartValidationState.UNABLE_TO_VERIFY_STOCK:
              return cartError(ErrorType.CartValidationError, {
                message: this.t("checkout:stockCantBeValidated")
              })
          }
        })
        .catch(err => {
          return cartError(ErrorType.CartValidationError, {
            message: "Something went wrong"
          })
        })
    } else {
      return cartError(ErrorType.CartNotFoundError, {
        message: this.t("checkout:yourBagIsEmpty")
      })
    }
  }
}
